import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Home.css';

const TEAM_IMAGE_SRC = '/team.jpg';
const TEAM_IMAGE_FALLBACK = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80';

const Home = () => {
  const [teamImgSrc, setTeamImgSrc] = useState(TEAM_IMAGE_SRC);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observers = sectionRefs.current
      .filter(Boolean)
      .map((el) => {
        const o = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) entry.target.classList.add('in-view');
          },
          { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
        );
        o.observe(el);
        return o;
      });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const handleBrochureDownload = () => {
    const brochurePath = '/helvion-brochure.pdf';
    const link = document.createElement('a');
    link.href = brochurePath;
    link.download = 'Helvion-Solutions-Brochure.pdf';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Helmet>
        <title>Helvion Solutions - IT Services & Software Development</title>
        <meta name="description" content="Helvion Solutions offers custom software development, web applications, cloud services, DevOps, and managed IT services. Transform your business with cutting-edge technology solutions." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Helvion Solutions",
            "url": "https://helvionsolutions.com",
            "logo": "https://helvionsolutions.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+44-7404666615",
              "contactType": "customer service",
              "email": "info@helvionsolutions.com"
            },
            "sameAs": []
          })}
        </script>
      </Helmet>

      <main className="home-page">
        {/* Hero with home.jpg image */}
        <section className="hero">
          <div className="hero-bg-shapes">
            <div className="shape shape-1" />
            <div className="shape shape-2" />
            <div className="shape shape-3" />
          </div>
          <div className="hero-container">
            <div className="hero-content">
              <span className="hero-badge animate-fade-in">Trusted IT Partner</span>
              <h1 className="hero-title">
                Transform Your Business with
                <span className="highlight"> Cutting-Edge IT Solutions</span>
              </h1>
              <p className="hero-subtitle">
                We deliver innovative software development, cloud services, and managed IT solutions
                that drive growth and efficiency for businesses worldwide.
              </p>
              <div className="hero-buttons">
                <Link to="/services" className="btn btn-primary btn-glow">
                  Explore Our Services
                </Link>
                <Link to="/contact" className="btn btn-secondary">
                  Get In Touch
                </Link>
              </div>
            </div>
            <div className="hero-image-wrap">
              <img src="/home.jpg.png" alt="Helvion Solutions - IT Services" className="hero-image" />
            </div>
          </div>
          <div className="hero-scroll-indicator">
            <span>Scroll</span>
            <div className="scroll-line" />
          </div>
        </section>

        {/* Services Overview */}
        <section className="section services-overview reveal" ref={(el) => (sectionRefs.current[1] = el)}>
          <div className="section-header">
            <span className="section-badge">What We Offer</span>
            <h2 className="section-title">Our Core Services</h2>
            <p className="section-subtitle">
              Comprehensive IT solutions tailored to your business needs
            </p>
          </div>
          <div className="services-grid">
            {[
              { num: '01', title: 'Custom Software & Web Application Development', desc: 'Tailored software solutions and web applications designed to meet your unique business requirements.' },
              { num: '02', title: 'Website & Mobile Application Development', desc: 'Responsive websites and native mobile apps that engage users and drive business results.' },
              { num: '03', title: 'Cloud Services (AWS, Azure, GCP)', desc: 'AWS, Azure, and GCP solutions for scalable, secure, and cost-effective cloud infrastructure.' },
              { num: '04', title: 'DevOps, CI/CD & Infrastructure Automation', desc: 'Infrastructure automation and continuous integration/deployment pipelines for faster delivery.' },
              { num: '05', title: 'Application Support, IT Support & Managed Services', desc: 'Comprehensive application support and managed IT services to keep your systems running smoothly.' },
            ].map((item, i) => (
              <div key={i} className="service-card" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="service-number">{item.num}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                <Link to="/services" className="service-link">Learn More →</Link>
              </div>
            ))}
          </div>
          <div className="services-cta">
            <Link to="/services" className="btn btn-primary">View All Services</Link>
          </div>
        </section>

        {/* Team & IT section with HD image */}
        <section className="team-showcase-section reveal" ref={(el) => (sectionRefs.current[0] = el)}>
          <div className="team-showcase-container">
            <div className="team-showcase-image-wrap">
              <img
                src={teamImgSrc}
                alt="Helvion Solutions - Expert IT Team"
                className="team-showcase-image"
                onError={() => setTeamImgSrc(TEAM_IMAGE_FALLBACK)}
              />
              <div className="team-showcase-image-overlay" />
            </div>
            <div className="team-showcase-content">
              <span className="section-badge">Our Infrastructure</span>
              <h2 className="team-showcase-title">Expert Team & Enterprise-Grade Infrastructure</h2>
              <p className="team-showcase-text">
                Our skilled professionals work from state-of-the-art facilities. We combine human expertise with robust infrastructure to deliver reliable, scalable solutions for your business.
              </p>
              <ul className="team-showcase-list">
                <li>Certified engineers and developers</li>
                <li>Secure, redundant operations</li>
                <li>24/7 monitoring and support</li>
                <li>Compliance-ready infrastructure</li>
              </ul>
              <Link to="/about" className="btn btn-primary">Meet Our Team</Link>
            </div>
          </div>
        </section>

        {/* Brochure Download */}
        <section className="section brochure-section reveal" ref={(el) => (sectionRefs.current[2] = el)}>
          <div className="brochure-container">
            <div className="brochure-content">
              <h2>Download Our Company Brochure</h2>
              <p>
                Learn more about Helvion Solutions, our services, expertise, and how we can help
                transform your business with innovative IT solutions.
              </p>
              <button onClick={handleBrochureDownload} className="btn btn-primary btn-glow">
                Download Brochure
              </button>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="section why-choose-section reveal" ref={(el) => (sectionRefs.current[3] = el)}>
          <div className="section-header">
            <span className="section-badge">Why Us</span>
            <h2 className="section-title">Why Choose Helvion Solutions</h2>
            <p className="section-subtitle">
              We combine technical expertise with business acumen to deliver solutions that matter
            </p>
          </div>
          <div className="why-choose-grid">
            {[
              { title: 'Expert Team', desc: 'Our team of experienced professionals brings deep expertise across multiple technology domains and industries.' },
              { title: 'Proven Track Record', desc: 'We have successfully delivered projects for businesses of all sizes, from startups to large enterprises.' },
              { title: 'Tailored Solutions', desc: 'Every project is unique. We take a customized approach to ensure solutions align with your specific goals.' },
              { title: 'Ongoing Support', desc: 'We provide comprehensive support and maintenance to ensure your systems continue to perform optimally.' },
            ].map((item, i) => (
              <div key={i} className="why-choose-item" style={{ animationDelay: `${i * 0.1}s` }}>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="section cta-section reveal" ref={(el) => (sectionRefs.current[4] = el)}>
          <div className="cta-content">
            <h2>Ready to Transform Your Business?</h2>
            <p>Let's discuss how our IT solutions can drive your success</p>
            <Link to="/contact" className="btn btn-primary btn-large btn-glow">
              Contact Us Today
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;
